import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { WidgetHeader, LiveIndicator } from '../WidgetHeader';
import { WidgetToolbar } from '../WidgetToolbar';
import { FeedMessage } from './FeedMessage';
import { DayDivider } from './DayDivider';
import { LastReadDivider } from './LastReadDivider';
import { NewMessagesPill } from './NewMessagesPill';
import { FeedLightbox } from './FeedLightbox';
import { FeedSettings } from './FeedSettings';
import { useWsjFeed } from '../../hooks/useWsjFeed';
import { useLastRead } from '../../hooks/useLastRead';
import { useWsjFeedNotifications } from '../../hooks/useWsjFeedNotifications';
import { navigateToTicker } from '../../utils/tickerLinks';
import type { FeedMessage as FeedMessageType } from '../../services/wsjFeedService';

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

interface WsjFeedWidgetProps {
  onRemove: () => void;
}

export function WsjFeedWidget({ onRemove }: WsjFeedWidgetProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isNearTop, setIsNearTop] = useState(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const {
    settings: notifSettings,
    isAudioPrimed,
    notificationPermission,
    toggleSound,
    toggleDesktop,
    notifyNewMessage,
  } = useWsjFeedNotifications();

  const {
    messages,
    isLoading,
    isConnected,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    reachedDayEnd,
    pendingNewCount,
    pendingSinceTime,
    clearPendingNew,
  } = useWsjFeed({ isNearTop, onNewMessage: notifyNewMessage });

  const { lastReadId, updateLastRead } = useLastRead();

  // Update last read when user is at the top and messages exist
  useEffect(() => {
    if (isNearTop && messages.length > 0) {
      updateLastRead(messages[0].id);
    }
  }, [isNearTop, messages, updateLastRead]);

  // Unread count: how many messages are newer than lastReadId
  const unreadCount = useMemo(() => {
    if (lastReadId === null || messages.length === 0) return 0;
    const idx = messages.findIndex(m => m.id === lastReadId);
    if (idx <= 0) return 0; // lastReadId is newest or not found
    return idx;
  }, [messages, lastReadId]);

  // Lightbox state
  const [lightbox, setLightbox] = useState<{ open: boolean; slides: { src: string }[]; index: number }>({
    open: false,
    slides: [],
    index: 0,
  });

  // Scroll handler: track nearTop, jump-to-latest visibility, clear pending, + infinite scroll at bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const nearTop = el.scrollTop < 100;
    setIsNearTop(nearTop);
    setShowJumpToLatest(el.scrollTop > el.clientHeight);

    // Clear pending new count when user scrolls to top
    if (nearTop) {
      clearPendingNew();
    }

    // Infinite scroll: fetch more when near bottom
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (hasNextPage && !isFetchingNextPage && !reachedDayEnd) {
        fetchNextPage();
      }
    }
  }, [hasNextPage, isFetchingNextPage, reachedDayEnd, fetchNextPage, clearPendingNew]);

  // Scroll to top (newest messages)
  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    clearPendingNew();
  }, [clearPendingNew]);

  // Image click handler
  const handleImageClick = useCallback((images: { url: string; filename: string }[], index: number) => {
    setLightbox({
      open: true,
      slides: images.map(img => ({ src: img.url })),
      index,
    });
  }, []);

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      <WidgetHeader title="WSJ Feed" indicator={<LiveIndicator isConnected={isConnected} />}>
        {unreadCount > 0 && (
          <span className="bg-bear-red text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </WidgetHeader>
      <WidgetToolbar
        onClose={onRemove}
        leftControls={
          <FeedSettings
            soundEnabled={notifSettings.soundEnabled}
            desktopEnabled={notifSettings.desktopEnabled}
            isAudioPrimed={isAudioPrimed}
            notificationPermission={notificationPermission}
            onToggleSound={toggleSound}
            onToggleDesktop={toggleDesktop}
          />
        }
      />

      {/* Message area */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-3 space-y-3 relative"
      >
        {/* New messages pill */}
        <NewMessagesPill count={pendingNewCount} sinceTime={pendingSinceTime} onClick={scrollToTop} />

        {isLoading ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            Loading...
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            No messages yet
          </div>
        ) : (
          <>
            {messages.map((msg: FeedMessageType, idx: number) => {
              const nextMsg = idx < messages.length - 1 ? messages[idx + 1] : null;
              // Show day divider when the NEXT message (older) is from a different day
              const showDayBoundary = nextMsg && formatDate(msg.created_at) !== formatDate(nextMsg.created_at);
              // Show last read divider after this message if it matches lastReadId
              // and it's not the newest message (no point showing at top)
              const showLastRead = lastReadId !== null && msg.id === lastReadId && idx > 0;

              return (
                <div key={msg.id}>
                  <FeedMessage
                    message={msg}
                    onImageClick={handleImageClick}
                    onTickerClick={navigateToTicker}
                  />
                  {showLastRead && <LastReadDivider />}
                  {showDayBoundary && (
                    <DayDivider label={formatDate(nextMsg.created_at)} />
                  )}
                </div>
              );
            })}

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextPage && (
              <div className="text-center py-2 text-gray-500 text-xs">
                Loading older messages...
              </div>
            )}

            {/* Discord link when we've reached the day boundary */}
            {reachedDayEnd && (
              <div className="text-center py-4 text-xs text-gray-500">
                For older messages, visit the{' '}
                <a
                  href="https://discord.com/channels/1409936885010141346/1458925204788219904"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-wheat hover:underline"
                >
                  WSJ Only channel in Discord
                </a>
              </div>
            )}
          </>
        )}

        {/* Jump to latest button */}
        {showJumpToLatest && (
          <button
            onClick={scrollToTop}
            className="sticky bottom-2 left-full -translate-x-full z-30 w-8 h-8 bg-accent-yellow hover:bg-wheat text-hot-tag-text rounded-full shadow-lg transition-colors flex items-center justify-center"
            title="Jump to latest"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 11V3M3 7l4-4 4 4" />
            </svg>
          </button>
        )}
      </div>

      <FeedLightbox
        open={lightbox.open}
        slides={lightbox.slides}
        index={lightbox.index}
        onClose={() => setLightbox(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
}
